import { useEffect, useState } from 'react';
import SimpleModal from 'components/SimpleModal';
import { Box, Chip, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrentContext } from 'hooks/useCurrentContext';
import useSearchTeamsByFieldsQuery from 'TeamModule/query/hooks/useSearchTeamsByFieldsQuery';
import { useUpdateTeamMutation } from 'TeamModule/query/hooks/useUpdateTeamMutation';

const NICKNAME_MAX_LENGTH = 50;

const dedupeAppend = (list: string[], value: string): string[] => {
  const trimmed = value.trim();
  if (!trimmed) return list;
  const lower = trimmed.toLowerCase();
  if (list.some((n) => n.toLowerCase() === lower)) return list;
  return [...list, trimmed];
};

/**
 * Normalize an incoming list of nicknames for local state hydration:
 * trim, drop empties/over-length entries, and dedupe case-insensitively.
 * Applied on initial load so odd backend data does not leak into state.
 *
 * Submit-time handling does not re-normalize or silently drop invalid values;
 * it validates the current state and surfaces an error instead.
 */
const normalizeNicknameList = (list: readonly string[] | null | undefined) => {
  const result: string[] = [];
  for (const value of list ?? []) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > NICKNAME_MAX_LENGTH) continue;
    const lower = trimmed.toLowerCase();
    if (result.some((n) => n.toLowerCase() === lower)) continue;
    result.push(trimmed);
  }
  return result;
};

export type EditNicknamesModalProps = {
  teamId: number;
  onClose: VoidFunction;
};

const EditNicknamesModal: React.FC<EditNicknamesModalProps> = ({ teamId, onClose }) => {
  const { tenant, realmId } = useCurrentContext();
  const queryEnabled = Boolean(tenant && realmId && teamId);
  const { teamsSearch, isLoading, isError } = useSearchTeamsByFieldsQuery({
    tenant,
    realmId,
    teamId: teamId.toString(),
    limit: 1,
    enabled: queryEnabled,
  });
  const galaxyTeam = teamsSearch?.data?.[0];
  // Only treat "no row" as "not found" once an actual lookup has run.
  // When the query is disabled (tenant/realmId still undefined),
  // isLoading/isError are both false but no request has happened yet.
  const teamNotFound = queryEnabled && !isLoading && !isError && !galaxyTeam;

  const [nicknames, setNicknames] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && galaxyTeam) {
      setNicknames(normalizeNicknameList(galaxyTeam.nicknames));
      setIsInitialized(true);
    }
  }, [galaxyTeam, isInitialized]);

  const { updateTeam, isUpdatingTeam } = useUpdateTeamMutation();

  const hasLoadedTeam = isInitialized && Boolean(galaxyTeam);
  const submitting = isUpdatingTeam;
  // Inputs and Update stay disabled until initial nicknames are loaded from
  // the backend, so a slow query can't clobber user edits (the load-sync
  // useEffect above overwrites local state the first time data arrives), and
  // so clicking Update without a matched team can't persist an empty array.
  const formDisabled = isLoading || !hasLoadedTeam || submitting;

  const addNickname = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (trimmed.length > NICKNAME_MAX_LENGTH) {
      setError(`Nicknames must be ${NICKNAME_MAX_LENGTH} characters or fewer.`);
      return;
    }
    setNicknames((prev) => dedupeAppend(prev, trimmed));
    setInput('');
    setError('');
  };

  const removeNickname = (value: string) => setNicknames((prev) => prev.filter((n) => n !== value));

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={() => {
        if (formDisabled) return;
        const pending = input.trim();
        let working = nicknames;
        if (pending) {
          if (pending.length > NICKNAME_MAX_LENGTH) {
            setError(`Nicknames must be ${NICKNAME_MAX_LENGTH} characters or fewer.`);
            return;
          }
          working = dedupeAppend(nicknames, pending);
          setNicknames(working);
          setInput('');
          setError('');
        }
        // Validate every entry in the final payload (not just the pending
        // input) so defensively-seeded backend data or a state edit that
        // bypassed the UI guards can't silently slip through.
        const offender = working.find(
          (n) => n.trim().length === 0 || n.length > NICKNAME_MAX_LENGTH,
        );
        if (offender) {
          setError(
            `"${offender}" is not a valid nickname (must be 1–${NICKNAME_MAX_LENGTH} characters).`,
          );
          return;
        }
        updateTeam([{ teamId: teamId.toString(), nicknames: working }], {
          onSuccess: () => onClose(),
        });
      }}
      headerText={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Edit Nicknames</span>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
      confirmButtonText="Update"
      disableConfirmButton={formDisabled}
      Body={
        <Box>
          {isLoading && (
            <Box
              sx={{
                mb: 1,
                color: 'text.secondary',
                typography: 'body2',
              }}
            >
              Loading team…
            </Box>
          )}
          {isError && (
            <Box
              sx={{
                mb: 1,
                color: 'error.main',
                typography: 'body2',
              }}
            >
              Failed to load team. Close the dialog and try again.
            </Box>
          )}
          {teamNotFound && (
            <Box
              sx={{
                mb: 1,
                color: 'error.main',
                typography: 'body2',
              }}
            >
              Team not found in this tenant/realm — nicknames cannot be edited here.
            </Box>
          )}
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              label="Enter nickname"
              fullWidth
              value={input}
              disabled={formDisabled}
              onChange={(e) => {
                if (error) setError('');
                setInput(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addNickname();
                }
              }}
              error={Boolean(error)}
              helperText={error || undefined}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
            }}
          >
            {nicknames.map((n) => (
              <Chip
                key={n}
                label={n}
                onDelete={formDisabled ? undefined : () => removeNickname(n)}
                color="primary"
                style={{ margin: 4 }}
              />
            ))}
          </Box>
        </Box>
      }
    />
  );
};

export default EditNicknamesModal;
export { EditNicknamesModal };
